import { uuidv7 } from "@luckymenu/core";
import type { KeyValueStorage, PushPermission } from "../adapters";

/**
 * When to offer push notifications (P3-T32, "at a sensible moment"): not at
 * sign-in, where the app already asks for sound, but once this person has
 * acted on a new order or a call on this device (they are on live service
 * and would want to hear the next one with the tab in the background), and
 * not again for 7 days after "Not now". Browsers let a site ask only once:
 * a "no" there is final until the person changes the site's settings.
 */

export const OFFER_AGAIN_MS = 7 * 24 * 60 * 60 * 1000;

export type OfferInput = {
	permission: PushPermission;
	/** The server has a VAPID key. */
	configured: boolean;
	/** Admins in a support session get no restaurant's pushes. */
	support: boolean;
	/** Decides diners' orders or answers calls. */
	canAct: boolean;
	/** Acted on an order or call on this device. */
	engaged: boolean;
	dismissedAt: number | null;
	now: number;
};

export function shouldOfferPush(input: OfferInput): boolean {
	return (
		input.permission === "default" &&
		input.configured &&
		!input.support &&
		input.canAct &&
		input.engaged &&
		(input.dismissedAt === null || input.now - input.dismissedAt >= OFFER_AGAIN_MS)
	);
}

export type PushStatus =
	/** The browser has no push (or the app gave no adapter). */
	| "unsupported"
	/** The server sends no web push (no VAPID key). */
	| "unavailable"
	| "off"
	| "on"
	/** The person said no in the browser. */
	| "denied";

/** Registered: the server has this device's subscription. Allowed but not registered is still off. */
export function pushStatus(
	permission: PushPermission,
	configured: boolean,
	support: boolean,
	registered: boolean,
): PushStatus {
	if (permission === "unsupported") return "unsupported";
	if (!configured || support) return "unavailable";
	if (permission === "granted") return registered ? "on" : "off";
	if (permission === "denied") return "denied";
	return "off";
}

export const DEVICE_ID_KEY = "luckymenu.deviceId";

/** This install's device ID: made once and kept, so registering again updates one row. */
export async function deviceIdOf(storage: KeyValueStorage): Promise<string> {
	const known = await storage.getItem(DEVICE_ID_KEY).catch(() => null);
	if (known) return known;
	const id = uuidv7();
	await storage.setItem(DEVICE_ID_KEY, id).catch(() => {});
	return id;
}
